import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, Store, MessageSquareQuote, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { errMsg, getBusiness, getPromptPreview, saveBusiness } from "@/lib/api";

const FIELDS = [
  ["name", "Nombre del negocio", "Ej. Café Aurora"],
  ["bot_name", "Nombre del asistente", "Ej. Aurora"],
  ["hours", "Horario de atención", "Lun a Sáb 9:00–18:00"],
  ["address", "Dirección", "Calle, ciudad"],
  ["phone", "Teléfono de contacto", ""],
  ["website", "Sitio web / redes", ""],
  ["payment_methods", "Métodos de pago", "Efectivo, tarjeta, transferencia"],
  ["tone", "Tono de voz", "Cercano y profesional"],
];
const CURRENCIES = ["USD", "MXN", "COP", "PEN", "CLP", "ARS", "EUR", "GTQ", "DOP", "BOB", "CRC", "UYU"];

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6 animate-rise">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Icon className="h-[18px] w-[18px]" /></div>
        <div><h3 className="font-display text-lg font-bold">{title}</h3>{hint && <p className="text-sm text-slate-500">{hint}</p>}</div>
      </div>
      {children}
    </section>
  );
}

export default function Training() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { getBusiness().then(setB).catch(() => toast.error("No se pudo cargar")); }, []);
  if (!b) return <div className="p-10 text-slate-400">Cargando…</div>;

  const set = (k) => (e) => setB((x) => ({ ...x, [k]: e.target.value }));
  const setFaq = (i, k, v) => setB((x) => ({ ...x, faqs: x.faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));

  const save = async () => {
    setSaving(true);
    try {
      await saveBusiness({ ...b, faqs: b.faqs.filter((f) => f.q.trim() && f.a.trim()) });
      toast.success("Bot entrenado con la nueva información");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const showPreview = async () => { await save(); setPreview((await getPromptPreview()).prompt); };

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="training-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <PageHeader title="Entrenar bot" subtitle="Todo lo que escribas aquí lo usa el bot para responder a tus clientes. Cuanta más información, mejores respuestas.">
          <Button variant="outline" data-testid="prompt-preview-button" onClick={showPreview} className="rounded-full"><Eye className="h-4 w-4 mr-1.5" />Ver lo que lee el bot</Button>
          <Button data-testid="save-training-button" onClick={save} disabled={saving} className="rounded-full bg-emerald-500 hover:bg-emerald-600"><Save className="h-4 w-4 mr-1.5" />{saving ? "Guardando…" : "Guardar"}</Button>
        </PageHeader>

        <div className="space-y-6">
          <Section icon={Store} title="Perfil del negocio" hint="Datos básicos que el bot compartirá cuando se los pidan.">
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map(([k, label, ph]) => (
                <div key={k}><Label>{label}</Label><Input data-testid={`business-${k}-input`} value={b[k]} onChange={set(k)} placeholder={ph} className="mt-1.5" /></div>
              ))}
              <div>
                <Label>Moneda</Label>
                <Select value={b.currency} onValueChange={(v) => setB((x) => ({ ...x, currency: v }))}>
                  <SelectTrigger data-testid="business-currency-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 grid gap-4">
              <div><Label>¿Qué hace tu negocio?</Label><Textarea data-testid="business-description-input" value={b.description} onChange={set("description")} rows={3} className="mt-1.5" placeholder="Describe tus productos o servicios, qué te diferencia…" /></div>
              <div><Label>Envíos y entregas</Label><Textarea data-testid="business-delivery-input" value={b.delivery} onChange={set("delivery")} rows={2} className="mt-1.5" /></div>
            </div>
          </Section>

          <Section icon={MessageSquareQuote} title="Preguntas frecuentes" hint="Respuestas exactas para las dudas más comunes.">
            <div className="space-y-3">
              {b.faqs.map((f, i) => (
                <div key={i} className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-3 items-start p-3 rounded-xl bg-slate-50" data-testid={`faq-row-${i}`}>
                  <Input data-testid={`faq-q-${i}`} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} placeholder="Pregunta" className="bg-white" />
                  <Textarea data-testid={`faq-a-${i}`} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} placeholder="Respuesta" rows={2} className="bg-white" />
                  <Button size="icon" variant="ghost" data-testid={`faq-delete-${i}`} onClick={() => setB((x) => ({ ...x, faqs: x.faqs.filter((_, j) => j !== i) }))} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" data-testid="add-faq-button" onClick={() => setB((x) => ({ ...x, faqs: [...x.faqs, { q: "", a: "" }] }))} className="mt-4 rounded-full"><Plus className="h-4 w-4 mr-1.5" />Agregar pregunta</Button>
          </Section>

          <Section icon={Sparkles} title="Comportamiento" hint="Cómo saluda y reglas especiales.">
            <div className="grid gap-4">
              <div><Label>Mensaje de bienvenida</Label><Textarea data-testid="business-welcome-input" value={b.welcome_message} onChange={set("welcome_message")} rows={2} className="mt-1.5" placeholder="¡Hola! Soy … ¿en qué te ayudo?" /></div>
              <div><Label>Instrucciones adicionales</Label><Textarea data-testid="business-extra-input" value={b.extra_instructions} onChange={set("extra_instructions")} rows={4} className="mt-1.5" placeholder="Ej. Ofrece siempre el combo del día. No hagas descuentos." /></div>
            </div>
          </Section>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="prompt-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Instrucciones que recibe el bot</DialogTitle></DialogHeader>
          <pre className="max-h-[60vh] overflow-y-auto scroll-thin whitespace-pre-wrap text-xs bg-slate-50 rounded-xl p-4 font-mono">{preview}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
